<template>
  <div class="card">
    <CardHeader :icon="icon" :title="title" :is-small="isTitleSmall" :data-qa="dataQa">
      <template #action-button>
        <slot name="action-button"></slot>
      </template>
      <template #tabs>
        <slot name="tabs"></slot>
      </template>
    </CardHeader>
    <div class="content">
      <div v-if="loading" class="loading">
        <LoaderItem />
      </div>
      <slot v-else-if="hasDataAndNotLoading" name="content"></slot>
      <div v-else class="no-data" data-qa="NO_DATA">
        <div class="no-data-icon">
          <SvgIcon value="Forgot" />
        </div>
        <h3 class="no-data-text">
          {{ noDataText }}
        </h3>
        <div v-if="hasNoDataSubTitle" class="body-regular-default sub-text">
          {{ noDataSubText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CardHeader, SvgIcon, LoaderItem } from '@lw-design-system/ui';

const { t, locale, getLocaleCookie, setLocale, setLocaleCookie } = useI18n();

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  hasData: {
    type: Boolean,
    default: true
  },
  listName: {
    type: String,
    default: ''
  },
  dataQa: {
    type: String,
    default: ''
  },
  isTitleSmall: {
    type: Boolean,
    default: false
  },
  hasPadding: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  hasNoDataSubTitle: {
    type: Boolean,
    default: true
  }
});

const noDataText = computed(() =>
  t('dataGrid.noDataText', { item: t(`dataGrid.text.${props.listName}`) })
);
const noDataSubText = computed(() =>
  t('dataGrid.noDataSubText', { item: t(`dataGrid.subText.${props.listName}`) })
);
const padding = computed(() => (props.hasPadding ? '1.5rem' : '0rem'));
const hasDataAndNotLoading = computed(() => props.hasData && !props.loading);

onMounted(() => {
  const cookie = getLocaleCookie();
  if (cookie && isSupportedLocale(cookie)) {
    setLocale(cookie);
  }
});

watch(locale, async (selectedLocale: string) => {
  if (!isSupportedLocale(selectedLocale)) return;
  await setLocale(selectedLocale);
  setLocaleCookie(selectedLocale);
});
</script>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  background-color: var(--card-background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow-card);
  flex: 1;
}

.content {
  padding: v-bind(padding);
  overflow-y: hidden;
  border-radius: 0 0 0.5rem 0.5rem;
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 3rem 0rem;
}

.no-data-icon {
  width: 22.5rem;
  height: 22.5rem;
}

.no-data-text {
  color: var(--text-default);
  text-align: center;
  max-width: 31.25rem;
  margin-bottom: 0.25rem;
}

.sub-text {
  color: var(--text-light);
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 1.5rem;
  height: 25rem;
}
</style>
